/**
 * Database Adapter - Handles communication with the database proxy API
 * This adapter abstracts the database operations and communicates with the backend
 * Uses the insertWorkflowDatabaseRecord API which handles all operations (insert, update, delete, get)
 *
 * Works in both browser (frontend) and Deno (backend) environments
 *
 * SECURITY NOTE for Deno backend:
 * In concurrent async environments, credentials are stored in a request-scoped context Map
 * (keyed by unique request ID) to prevent credential leakage between requests.
 *
 * For maximum security in async operations, functions can:
 * 1. Pass context directly: await dbAdapter.callApi(op, table, data, where, records, { apiKey, authToken })
 * 2. Or set instance credentials: dbAdapter.setCredentials(apiKey, authToken)
 */

// Environment detection
const isBrowser = typeof window !== "undefined";
const isDeno = typeof Deno !== "undefined";


class DatabaseAdapter {
    constructor() {
        // Get API URL based on environment
        if (isBrowser) {
            const hostname = window?.location?.hostname ?? "";
            const isStaging =
                hostname.includes("staging-apps.agentui.ai") ||
                hostname.includes("staging-api.agentui.ai") ||
                hostname === "staging.agentui.ai";
            this.apiUrl =
                hostname === "localhost" || hostname?.includes("agentui.test")
                    ? "http://localhost:3333"
                    : isStaging
                    ? "https://staging-api.agentui.ai"
                    : "https://api.agentui.ai";
            this.workflowId = window.config?.workflowId;
            // In browser, apiKey is read dynamically via getApiKey()
        } else if (isDeno) {
            this.apiUrl =
                Deno.env.get("API_BASE_URL") || "https://api.agentui.ai";
            this.workflowId = Deno.env.get("WORKFLOW_ID");
            // In Deno, apiKey and token are read dynamically from request-scoped globals
        } else {
            // Node.js fallback
            this.apiUrl = process.env.API_BASE_URL || "https://api.agentui.ai";
            this.workflowId = process.env.WORKFLOW_ID;
            this.apiKey = process.env.AGENTUI_API_KEY;
        }

        // Rate limiting configuration
        // Track calls per entity (collection/table name)
        // Structure: Map<entityName, { count: number, windowStart: number, alertShown: boolean }>
        this._callTracker = new Map();

        // Track which entities have shown alerts in current windows
        // Format: "entityName:windowStart" to ensure alerts reset per window
        this._alertShownSet = new Set();

        // Rate limit configuration — a leaky TOKEN BUCKET, not a wall-clock
        // window. Capacity is `maxCalls`; one token is restored every
        // `windowMs / maxCalls` ms, so an app that empties the bucket recovers
        // progressively instead of staying dead until an arbitrary boundary
        // rolls over.
        //
        // An over-limit call is REJECTED IMMEDIATELY. The limiter never delays
        // a call: it reports `retryAfter` and lets the caller decide whether to
        // back off, drop the request, or surface it. Making the SDK wait would
        // hide the problem behind a slow page and take the choice away.
        this._rateLimitConfig = {
            maxCalls: 200,
            windowMs: 60000, // 1 minute
        };

        // Call deduplication: prevent duplicate calls within a short time window
        // Structure: Map<callSignature, { promise: Promise, timestamp: number }>
        this._callDeduplicationMap = new Map();
        this._debounceTimeoutMs = 50; // 50ms debounce window

        // Result cache: cache successful results for faster subsequent calls
        // Structure: Map<callSignature, { result: any, timestamp: number }>
        this._resultCacheMap = new Map();
        this._cacheTimeoutMs = 300; // 300ms cache window

        // Threshold to warn about large result sets (AI hint)
        this._largeResultSetThreshold = 50;

        // Retry configuration for transient failures
        this._retryConfig = {
            maxRetries: 3,
            baseDelayMs: 300,
            maxDelayMs: 5000,
        };

        // Fetch timeout configuration
        this._fetchTimeoutMs = 30000; // 30 seconds

        // Rapid-call detection for debounce suggestions
        // Structure: Map<entityName, Array<{ timestamp, filterString }>>
        this._rapidCallEntries = new Map();
        this._rapidCallSuggestedSet = new Set();
    }

    /**
     * Get request context from the secure context map (Deno only)
     * This prevents credential leakage between concurrent requests
     */
    /** True when AsyncLocalStorage still names a request whose context was released. */
    _isOrphanedRequest() {
        if (!isDeno) return false;
        const storage = globalThis.__AGENTUI_REQUEST_ID_STORAGE__;
        const requestId = storage ? storage.getStore() : null;
        const contexts = globalThis.__AGENTUI_REQUEST_CONTEXTS__;
        return Boolean(requestId && contexts && !contexts.has(requestId));
    }

    _getRequestContext() {
        if (!isDeno) return null;

        // Retrieve requestId from AsyncLocalStorage (set in server.ts)
        const storage = globalThis.__AGENTUI_REQUEST_ID_STORAGE__;
        const requestId = storage ? storage.getStore() : null;
        const contexts = globalThis.__AGENTUI_REQUEST_CONTEXTS__;

        if (!requestId || !contexts) return null;

        return contexts.get(requestId) || null;
    }

    /**
     * Get the API key - checks request-scoped context first (for Deno), then config/env
     * Uses secure request context map to prevent credential leakage between requests
     */
    getApiKey() {
        if (isBrowser) {
            return window.config?.apiKey || null;
        } else if (isDeno) {
            // In Deno, get API key from secure request-scoped context
            const context = this._getRequestContext();
            if (context?.apiKey) {
                return context.apiKey;
            }
            // Detached work from a finished request gets no credential at all
            // (fail closed) rather than the raw container secret.
            if (!context && this._isOrphanedRequest()) return null;
            // No caller credential — this is a cron run, a webhook, or one
            // function calling another. Prefer the request's signed s2s token
            // over the raw secret: the token can be accompanied by a signed
            // environment attestation, and the raw secret cannot, which is why
            // those calls used to read and write the PRODUCTION database no
            // matter which environment they ran in.
            if (context?.s2sToken) {
                return context.s2sToken;
            }
            // Fall back to env var if no request context
            return Deno.env.get("AGENTUI_API_KEY") || null;
        }
        return this.apiKey || null;
    }

    getServerKey() {
        if (isBrowser) {
            // always null
            return null;
        } else if (isDeno) {
            // A request that must run AS a user (custom MCP tools) opts out of
            // the container's service key: with it, every entity call would be
            // service role and Data Security would not apply to the tool.
            const context = this._getRequestContext();
            if (context?.noServiceKey === true) return null;
            // A request id with no context: the request finished and its
            // context was released, but a detached promise from that request
            // is still running. It must not inherit the container's key.
            if (!context && this._isOrphanedRequest()) return null;
            return Deno.env.get("AGENTUI_SERVER_KEY") || null;
        }
        return process.env.AGENTUI_SERVER_KEY || null;
    }

    /**
     * Get the Bearer token from localStorage (browser) or request-scoped context (Deno)
     * Uses secure request context map to prevent credential leakage between requests
     */
    getToken() {
        const workflowId = this.workflowId;
        if (!workflowId) {
            return null;
        }

        if (isBrowser) {
            const tokenKey = `workflow-auth-${workflowId}`;
            return localStorage.getItem(tokenKey);
        } else if (isDeno) {
            // In Deno, get auth token from secure request-scoped context
            const context = this._getRequestContext();
            return context?.authToken || null;
        }

        // Node.js fallback - no token
        return null;
    }

    /**
     * Set explicit credentials for this adapter instance
     * Use this when you have the credentials from the request context
     * to avoid any race conditions with concurrent requests
     */
    setCredentials(apiKey, authToken) {
        this._explicitApiKey = apiKey;
        this._explicitAuthToken = authToken;
    }

    /**
     * Clear explicit credentials
     */
    clearCredentials() {
        this._explicitApiKey = undefined;
        this._explicitAuthToken = undefined;
    }

    /**
     * Configure rate limiting
     * @param {number} maxCalls - Bucket capacity (calls per window)
     * @param {number} windowMs - Time to fully refill the bucket, in ms
     */
    setRateLimit(maxCalls, windowMs) {
        // Coerced, not trusted. A non-integer capacity breaks the
        // `count + 1 > maxCalls` / `count < maxCalls` complement the limiter
        // relies on, and a zero or missing window makes msPerToken Infinity or
        // NaN — which used to hand callers `retryAfterMs: 0` while the bucket
        // was permanently empty, i.e. a spin loop.
        this._rateLimitConfig = {
            maxCalls: Number.isFinite(maxCalls) ? Math.max(0, Math.floor(maxCalls)) : 0,
            windowMs:
                Number.isFinite(windowMs) && windowMs > 0 ? windowMs : 60000,
        };

        // Reconcile live buckets with the new capacity. A bucket holding 200
        // tokens when capacity drops to 1 is 199 tokens in debt that nothing
        // accounts for: _retryAfterMsFor reports the time to the NEXT token
        // (one interval) while the first admissible call is 199 intervals away.
        // Every rejection would advertise a wait that is orders of magnitude
        // short, which is worse than no retryAfter at all.
        const cap = this._rateLimitConfig.maxCalls;
        for (const tracker of this._callTracker.values()) {
            if (tracker.count > cap) tracker.count = cap;
            tracker.windowStart = this._now();
        }
    }

    /**
     * Current time. A single seam so the limiter can be driven deterministically
     * in tests instead of racing a real 300ms token interval.
     * @returns {number}
     */
    _now() {
        return Date.now();
    }

    /**
     * Quantize a timestamp to the start of its fixed alert window.
     * The token bucket slides `windowStart` continuously, so it can no longer
     * be used to dedupe user-facing alerts — it changes every token interval,
     * which turned one modal per minute into one every 300ms.
     * @param {number} now
     * @returns {number}
     */
    _alertWindowStart(now = this._now()) {
        const { windowMs } = this._rateLimitConfig;
        if (!(windowMs > 0)) return 0;
        return Math.floor(now / windowMs) * windowMs;
    }

    /**
     * Dedup key for the preview's blocking rate-limit modal. The ONLY place the
     * key is built — it must never be derived from tracker.windowStart, which
     * the bucket slides every token interval.
     * @param {string} entityName
     * @returns {string}
     */
    _alertKeyFor(entityName) {
        return `${entityName}:${this._alertWindowStart()}`;
    }

    /**
     * Milliseconds of elapsed time that restore one token.
     * @returns {number}
     */
    _msPerToken() {
        const { maxCalls, windowMs } = this._rateLimitConfig;
        if (!(maxCalls > 0)) return Infinity;
        return windowMs / maxCalls;
    }

    /**
     * Get (creating if needed) the bucket for an entity.
     * @param {string} entityName
     * @returns {object}
     */
    _ensureTracker(entityName) {
        let tracker = this._callTracker.get(entityName);
        if (!tracker) {
            tracker = {
                count: 0,
                windowStart: this._now(),
                alertShown: false,
            };
            this._callTracker.set(entityName, tracker);
        }
        return tracker;
    }

    /**
     * Drain the bucket by however many tokens have been restored since the
     * last drain. Advances windowStart by whole token intervals only, so no
     * fractional time is lost between calls.
     * @param {object} tracker
     * @param {number} now
     */
    _refillTracker(tracker, now = this._now()) {
        const msPerToken = this._msPerToken();
        if (!isFinite(msPerToken) || msPerToken <= 0) return;

        const elapsed = now - tracker.windowStart;
        if (elapsed === 0) return;
        if (elapsed < 0) {
            // Wall clock moved backwards (NTP correction, laptop resume). Left
            // alone, windowStart sits in the future and the bucket never
            // refills until real time catches up — the entity would appear
            // permanently rate limited. Re-anchor instead.
            tracker.windowStart = now;
            return;
        }

        const restored = Math.floor(elapsed / msPerToken);
        if (restored <= 0) return;

        tracker.count = Math.max(0, tracker.count - restored);
        if (tracker.count === 0) {
            // Empty bucket: snap forward so idle time cannot bank credit.
            tracker.windowStart = now;
        } else {
            tracker.windowStart += restored * msPerToken;
        }
    }

    /**
     * How long until this entity has a token again.
     * @param {string} entityName
     * @returns {number} - ms (0 when a call would be admitted right now)
     */
    _retryAfterMsFor(entityName) {
        const { maxCalls, windowMs } = this._rateLimitConfig;
        // Never 0 and never NaN: a blocked caller that is told to wait 0ms
        // spins. `windowMs` can be anything if _rateLimitConfig was assigned
        // directly rather than through setRateLimit.
        if (!(maxCalls > 0)) return windowMs > 0 ? windowMs : Infinity;

        const tracker = this._callTracker.get(entityName);
        if (!tracker) return 0;

        const now = this._now();
        this._refillTracker(tracker, now);
        // Exact complement of _checkRateLimit's admit test — not `count <
        // maxCalls`, which disagrees with it for a non-integer capacity.
        if (tracker.count + 1 <= maxCalls) return 0;

        const msPerToken = this._msPerToken();
        if (!isFinite(msPerToken) || msPerToken <= 0) {
            // _refillTracker bails on the same condition, so this bucket will
            // never refill. 0 would mean "retry now" and spin forever.
            return windowMs > 0 ? windowMs : Infinity;
        }

        // windowStart is the anchor of the last whole token drained, so the
        // next one matures one full interval after it. Refill ran first, so
        // sinceAnchor is always < msPerToken here and this stays positive.
        const sinceAnchor = Math.max(0, now - tracker.windowStart);
        return Math.ceil(msPerToken - sinceAnchor);
    }

    /**
     * Build the AI- and human-actionable rate limit error.
     * @param {string} entityName
     * @param {number} retryAfterMs
     * @param {string} reason
     * @returns {Error}
     */
    _rateLimitError(entityName, retryAfterMs, reason) {
        const retryAfter = Math.max(1, Math.ceil(retryAfterMs / 1000));
        const error = new Error(
            `Rate limit exceeded for entity "${entityName}": ${reason}. ` +
                `The limit is ${this._rateLimitConfig.maxCalls} calls per ` +
                `${this._rateLimitConfig.windowMs / 1000} seconds per entity. ` +
                `Please retry after ${retryAfter}s, or reduce how often this ` +
                `table is read — cache results, debounce search inputs, ` +
                `paginate instead of refetching every record, or batch writes ` +
                `into one bulk call.`
        );
        error.code = "RATE_LIMITED";
        error.entity = entityName;
        error.retryAfterMs = retryAfterMs;
        error.retryAfter = retryAfter;
        return error;
    }

    /**
     * Admit this call, or reject it outright. NEVER delays: the limiter reports
     * `retryAfter` and hands the decision back to the caller.
     *
     * Synchronous on purpose. callApi's dedup lookup and its registration have
     * to sit in one uninterrupted span — an `await` between them lets every
     * concurrent duplicate miss the 50ms dedup window, turning one StrictMode
     * double render into two fetches and two spent tokens.
     *
     * @param {string} entityName
     * @returns {void}
     * @throws {Error} code RATE_LIMITED, carrying retryAfter / retryAfterMs
     */
    _acquireSlot(entityName) {
        const { maxCalls, windowMs } = this._rateLimitConfig;

        if (!(maxCalls > 0)) {
            // _handleRateLimitExceeded no-ops without a tracker, and nothing
            // creates one on this path.
            this._ensureTracker(entityName);
            this._handleRateLimitExceeded(entityName);
            throw this._rateLimitError(
                entityName,
                // Same reason as _retryAfterMsFor: a raw 0 here becomes a
                // zero-millisecond backoff and a spin loop.
                windowMs > 0 ? windowMs : Infinity,
                "database access is disabled (rate limit is 0)"
            );
        }

        if (this._checkRateLimit(entityName)) return;

        const retryAfterMs = this._retryAfterMsFor(entityName);
        this._handleRateLimitExceeded(entityName);
        throw this._rateLimitError(
            entityName,
            retryAfterMs,
            `the bucket is empty and the next slot is ${retryAfterMs}ms away`
        );
    }

    /**
     * Clean up expired alert entries from the alert set
     * Removes entries for windows that have expired
     */
    _cleanupExpiredAlerts() {
        const now = this._now();
        const expiredKeys = [];

        // Check all alert entries and find expired ones
        this._alertShownSet.forEach((alertKey) => {
            // Parse entityName and windowStart from key format: "entityName:windowStart"
            // Use lastIndexOf to handle entity names that might contain colons
            const lastColonIndex = alertKey.lastIndexOf(":");
            if (lastColonIndex > 0 && lastColonIndex < alertKey.length - 1) {
                const windowStart = parseInt(
                    alertKey.substring(lastColonIndex + 1),
                    10
                );
                if (
                    !isNaN(windowStart) &&
                    now - windowStart >= this._rateLimitConfig.windowMs
                ) {
                    // Keys are quantized window origins, so this is exact.
                    expiredKeys.push(alertKey);
                }
            }
        });

        // Remove expired entries
        expiredKeys.forEach((key) => this._alertShownSet.delete(key));
    }

    /**
     * Would this call be admitted right now? Takes the token when it would.
     *
     * A denied call must NOT consume a token. The old fixed-window counter kept
     * incrementing past the limit, so a rejected burst pushed every later caller
     * further out and the entity stayed dead well past its recovery point.
     *
     * @param {string} entityName - Name of the entity/collection
     * @returns {boolean} - true if within rate limit, false if exceeded
     */
    _checkRateLimit(entityName) {
        const { maxCalls } = this._rateLimitConfig;
        if (!(maxCalls > 0)) return false;

        const tracker = this._ensureTracker(entityName);
        this._refillTracker(tracker);

        if (tracker.count + 1 > maxCalls) {
            // The bucket caps `count` at maxCalls, so `count` alone can no
            // longer distinguish a 201-call overshoot from a 100,000-call
            // render loop. That magnitude is the most useful signal the AI
            // gets, so track it separately instead of letting the bucket carry
            // the debt (which is what made a rejected burst poison recovery).
            tracker.denied = (tracker.denied || 0) + 1;
            console.log(
                `Rate limit exceeded for ${entityName}: ${tracker.count + 1}`
            );
            return false;
        }
        tracker.denied = 0;

        if (tracker.alertShown === undefined) tracker.alertShown = false;
        tracker.count++;
        return true;
    }

    /**
     * Get current call count for an entity
     * @param {string} entityName - Name of the entity/collection
     * @returns {number} - Current call count in the current time window
     */
    getCallCount(entityName) {
        const tracker = this._callTracker.get(entityName);
        if (!tracker) return 0;

        // Report the bucket as it stands right now, after refill.
        this._refillTracker(tracker);
        return tracker.count;
    }

    /**
     * Get call statistics for all entities
     * @returns {object} - Object mapping entity names to their call counts
     */
    getCallStatistics() {
        const stats = {};
        const now = this._now();

        this._callTracker.forEach((tracker, entityName) => {
            this._refillTracker(tracker, now);
            // A fully drained bucket is not "active" — omit it.
            if (tracker.count > 0) {
                // Read retryAfterMs FIRST: it re-reads the clock and refills
                // again, so building it inline would let a token mature between
                // fields and report `count: 200, retryAfterMs: 0`.
                const retryAfterMs = this._retryAfterMsFor(entityName);
                stats[entityName] = {
                    count: tracker.count,
                    limit: this._rateLimitConfig.maxCalls,
                    windowStart: new Date(tracker.windowStart).toISOString(),
                    windowMs: this._rateLimitConfig.windowMs,
                    retryAfterMs,
                };
            }
        });

        return stats;
    }

    /**
     * Reset call tracker for a specific entity or all entities
     * @param {string|null} entityName - Entity name to reset, or null to reset all
     */
    resetCallTracker(entityName = null) {
        if (entityName) {
            // Remove all alert entries for this entity
            const tracker = this._callTracker.get(entityName);
            if (tracker) {
                const alertKey = this._alertKeyFor(entityName);
                this._alertShownSet.delete(alertKey);
            }
            this._callTracker.delete(entityName);
        } else {
            // Clear all trackers and alert entries
            this._callTracker.clear();
            this._alertShownSet.clear();
        }
    }

    /**
     * Clear the result cache
     * Useful when you want to force fresh data retrieval
     */
    clearCache() {
        this._resultCacheMap.clear();
        this._callDeduplicationMap.clear();
    }

    /**
     * Clear cache entries for a specific table/collection
     * Used when write operations occur to invalidate cached reads for that table
     * @param {string} tableName - Name of the table/collection to clear cache for
     */
    _clearCacheForTable(tableName) {
        if (!tableName) return;

        const keysToDelete = [];

        // Check cache map for entries matching this table
        this._resultCacheMap.forEach((entry, signature) => {
            try {
                const payload = JSON.parse(signature);
                if (payload.tableName === tableName) {
                    keysToDelete.push(signature);
                }
            } catch (e) {
                // If signature can't be parsed, skip it
            }
        });

        // Check deduplication map for entries matching this table
        this._callDeduplicationMap.forEach((entry, signature) => {
            try {
                const payload = JSON.parse(signature);
                if (payload.tableName === tableName) {
                    keysToDelete.push(signature);
                }
            } catch (e) {
                // If signature can't be parsed, skip it
            }
        });

        // Delete matching entries
        keysToDelete.forEach((key) => {
            this._resultCacheMap.delete(key);
            this._callDeduplicationMap.delete(key);
        });
    }

    /**
     * Generate a unique signature for a call based on its parameters
     * This excludes credentials (apiKey, authToken) as they don't affect call identity
     * @param {string} operation - The operation type
     * @param {string} tableName - Name of the table/collection
     * @param {object} data - Data for the operation
     * @param {object} where - Where conditions
     * @param {array} records - Array of records for bulk operations
     * @param {object} options - Additional options (excluding apiKey/authToken)
     * @returns {string} - Unique call signature
     */
    /**
     * Generate a unique signature for a call based on its parameters.
     * Sorts keys to ensure {a:1, b:2} and {b:2, a:1} produce the same signature.
     * Includes user identifier to ensure cache is scoped to the user session (for Deno)
     */
    _generateCallSignature(
        operation,
        tableName,
        data,
        where,
        records,
        options
    ) {
        // Deep sort keys for stable stringification
        const stabilize = (obj) => {
            if (obj === null || typeof obj !== "object" || Array.isArray(obj)) {
                return obj;
            }
            return Object.keys(obj)
                .sort()
                .reduce((acc, key) => {
                    acc[key] = stabilize(obj[key]);
                    return acc;
                }, {});
        };

        const cleanOptions = options ? { ...options } : null;
        if (cleanOptions) {
            delete cleanOptions.apiKey;
            delete cleanOptions.authToken;
        }

        // Get request context to scope cache to user session (Deno only)
        const context = this._getRequestContext();
        const userIdentifier = context?.authToken || "anonymous"; // Ensure cache is scoped to the user session

        const payload = {
            userIdentifier,
            operation,
            tableName,
            data: data ? stabilize(data) : null,
            where: where ? stabilize(where) : null,
            records: records ? records.map((r) => stabilize(r)) : null,
            options: cleanOptions ? stabilize(cleanOptions) : null,
        };

        return JSON.stringify(payload);
    }

    /**
     * Clean up expired call entries from the deduplication map and cache
     * Removes entries older than their respective timeouts
     */
    _cleanupExpiredCalls() {
        const now = this._now();
        const expiredDebounceKeys = [];
        const expiredCacheKeys = [];

        // Clean up expired debounce entries
        this._callDeduplicationMap.forEach((entry, signature) => {
            if (now - entry.timestamp >= this._debounceTimeoutMs) {
                expiredDebounceKeys.push(signature);
            }
        });

        // Clean up expired cache entries
        this._resultCacheMap.forEach((entry, signature) => {
            if (now - entry.timestamp >= this._cacheTimeoutMs) {
                expiredCacheKeys.push(signature);
            }
        });

        expiredDebounceKeys.forEach((key) =>
            this._callDeduplicationMap.delete(key)
        );
        expiredCacheKeys.forEach((key) => this._resultCacheMap.delete(key));
    }

    /**
     * Handle rate limit exceeded - log error and show alert in browser
     * @param {string} entityName - Name of the entity/collection
     */
    _handleRateLimitExceeded(entityName) {
        const tracker = this._callTracker.get(entityName);
        if (!tracker) return; // Safety check

        const denied = tracker.denied || 0;
        const message = `Rate limit exceeded for entity "${entityName}": ${
            tracker.count
        }/${this._rateLimitConfig.maxCalls} tokens spent in ${
            this._rateLimitConfig.windowMs / 1000
        } seconds, ${denied} further call${denied === 1 ? "" : "s"} rejected`;

        // Window-scoped, not lifetime-scoped. The old code minted a fresh
        // tracker object every window rollover, which reset these flags; the
        // bucket mutates one tracker in place forever. Without this the AI
        // nudge and the telemetry fire once per page load — and in a warm Deno
        // isolate, once for every end-user it ever serves.
        const currentWindow = this._alertWindowStart();

        // Log error with entity name (always log, even if alert already shown)
        console.error(`[Rate Limit] ${message}`);

        // The only remaining caller of the alert reaper — _checkRateLimit is no
        // longer on the hot path, so without this _alertShownSet grows forever.
        this._cleanupExpiredAlerts();

        // Send suggestion to the AI via console.human
        if (tracker.humanSuggestedWindow !== currentWindow) {
            tracker.humanSuggestedWindow = currentWindow;
            const suggestion = `The "${entityName}" table hit the rate limit (${
                this._rateLimitConfig.maxCalls
            } calls per ${
                this._rateLimitConfig.windowMs / 1000
            }s) — ${denied} call${
                denied === 1 ? " was" : "s were"
            } rejected. Reduce the frequency of database calls — add caching, debounce search inputs, paginate instead of refetching all records, or batch multiple operations into one.`;
            if (typeof console.human === "function") {
                console.human(suggestion, {
                    type: "rate_limit_exceeded",
                    entity: entityName,
                    callCount: tracker.count,
                    deniedCount: denied,
                    limit: this._rateLimitConfig.maxCalls,
                    suggestion,
                    actionable: true,
                });
            }
        }

        // Report rate limit to backend telemetry once per entity per window
        if (
            typeof window !== "undefined" &&
            window.reportErrorToBackend &&
            tracker.reportedWindow !== currentWindow
        ) {
            tracker.reportedWindow = currentWindow;
            window.reportErrorToBackend(message, "DATABASE_RATE_LIMIT", {
                entityName,
                callCount: tracker.count,
                deniedCount: denied,
                limit: this._rateLimitConfig.maxCalls,
            });
        }

        if (isBrowser) {
            // Check if query parameter has isInFrame - check both URLSearchParams and direct search
            const urlParams = new URLSearchParams(window.location.search);
            const searchString = window.location.search || "";
            const hasIsInFrameParam =
                urlParams.has("isInFrame") ||
                searchString.includes("isInFrame=true") ||
                searchString.includes("isInFrame=1");

            // Show alert if isInFrame parameter is present
            if (hasIsInFrameParam) {
                // Quantized to a FIXED window, not tracker.windowStart: the
                // bucket slides that forward every token interval, which would
                // mint a fresh key — and a fresh blocking modal — every 300ms.
                const alertKey = this._alertKeyFor(entityName);

                // Check if alert already shown for this entity in this window
                if (!this._alertShownSet.has(alertKey)) {
                    // Add to set immediately before showing alert to prevent race conditions
                    this._alertShownSet.add(alertKey);
                    tracker.alertShown = true; // Also update tracker for consistency

                    // Use setTimeout to ensure alert shows even if error is thrown immediately after
                    setTimeout(() => {
                        alert(
                            `Rate Limit Exceeded\n\n${message}\n\nPlease tell the AI to reduce the frequency of database operations for this ${entityName}.`
                        );
                    }, 0);
                }
            }
        }
    }

    /**
     * Determine if an error is retryable (transient)
     * @param {Error} error - The error to check
     * @param {number} httpStatus - HTTP status code if available
     * @returns {boolean}
     */
    _isRetryableError(error, httpStatus) {
        // Network errors (fetch failed, timeout, etc.)
        if (!httpStatus && error.name !== "AbortError") return true;
        // Server errors (5xx) are retryable
        if (httpStatus >= 500 && httpStatus < 600) return true;
        // 429 Too Many Requests (server-side rate limit)
        if (httpStatus === 429) return true;
        // 408 Request Timeout
        if (httpStatus === 408) return true;
        return false;
    }

    /**
     * Execute fetch with a timeout using AbortController
     * @param {string} endpoint - The URL to fetch
     * @param {object} fetchOptions - Options to pass to fetch
     * @returns {Promise<Response>}
     */
    async _fetchWithTimeout(endpoint, fetchOptions) {
        const controller = new AbortController();
        const timeoutId = setTimeout(
            () => controller.abort(),
            this._fetchTimeoutMs
        );
        try {
            const response = await fetch(endpoint, {
                ...fetchOptions,
                signal: controller.signal,
            });
            return response;
        } catch (error) {
            if (error.name === "AbortError") {
                throw new Error(
                    `Request timed out after ${
                        this._fetchTimeoutMs / 1000
                    } seconds`
                );
            }
            throw error;
        } finally {
            clearTimeout(timeoutId);
        }
    }

    /**
     * Extract string values from a filter object for prefix comparison
     * e.g. { name: { $icontains: "rea" } } => "rea"
     * @param {object} where - Filter conditions
     * @returns {string} Concatenated string values from the filter
     */
    _extractFilterStrings(where) {
        if (!where || typeof where !== "object") return "";
        const strings = [];
        const extract = (obj) => {
            for (const val of Object.values(obj)) {
                if (typeof val === "string") strings.push(val);
                else if (val && typeof val === "object") extract(val);
            }
        };
        extract(where);
        return strings.join("|");
    }

    /**
     * Check if filter strings show an incremental prefix pattern
     * e.g. ["r", "re", "rea", "reac", "react"] — each is a prefix of the next
     * @param {Array<string>} filterStrings - Recent filter string values
     * @returns {boolean} True if pattern looks like search-as-you-type
     */
    _isIncrementalPattern(filterStrings) {
        if (filterStrings.length < 3) return false;
        let prefixCount = 0;
        for (let i = 1; i < filterStrings.length; i++) {
            const prev = filterStrings[i - 1];
            const curr = filterStrings[i];
            if (!prev || !curr) continue;
            // Check if current starts with previous (typing) or previous starts with current (deleting)
            if (curr.startsWith(prev) || prev.startsWith(curr)) {
                prefixCount++;
            }
        }
        // At least 60% of transitions should be incremental
        return prefixCount >= (filterStrings.length - 1) * 0.6;
    }

    /**
     * Detect rapid successive read calls that indicate missing debounce/throttle
     * Looks for incremental filter patterns (search-as-you-type) to avoid
     * false positives from legitimate rapid calls (e.g., component mount)
     * @param {string} entityName - Name of the entity/collection
     * @param {number} now - Current timestamp
     * @param {object} where - Filter conditions
     */
    _detectRapidCalls(entityName, now, where) {
        if (this._rapidCallSuggestedSet.has(entityName)) return;

        const filterString = this._extractFilterStrings(where);
        let entries = this._rapidCallEntries.get(entityName);
        if (!entries) {
            entries = [];
            this._rapidCallEntries.set(entityName, entries);
        }

        entries.push({ timestamp: now, filterString });

        // Keep only entries from the last 3 seconds
        const cutoff = now - 3000;
        while (entries.length > 0 && entries[0].timestamp < cutoff) {
            entries.shift();
        }

        // Need at least 5 calls in the window
        if (entries.length < 5) return;

        // Check if filters are changing (not identical repeated calls — those are handled by dedup)
        const filterStrings = entries.map((e) => e.filterString);
        const uniqueFilters = new Set(filterStrings);
        if (uniqueFilters.size < 3) return; // Same filter repeated = not search-as-you-type

        // Check for incremental prefix pattern (strong signal)
        if (this._isIncrementalPattern(filterStrings)) {
            this._rapidCallSuggestedSet.add(entityName);
            const suggestion = `The "${entityName}" table is being queried on every keystroke (${entries.length} calls in under 3 seconds with incrementally changing filters). Add a debounce (300-500ms) to the search input so it only queries after the user stops typing. This will reduce unnecessary API calls and improve performance.`;
            if (typeof console.human === "function") {
                console.human(suggestion, {
                    type: "debounce_suggestion",
                    entity: entityName,
                    callCount: entries.length,
                    suggestion,
                    actionable: true,
                });
            }
        }
    }

    /**
     * Sleep for a given number of milliseconds
     * @param {number} ms
     * @returns {Promise<void>}
     */
    _sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    /**
     * Calculate delay for exponential backoff with jitter
     * @param {number} attempt - Current retry attempt (0-based)
     * @returns {number} - Delay in milliseconds
     */
    _getRetryDelay(attempt) {
        const delay = Math.min(
            this._retryConfig.baseDelayMs * Math.pow(2, attempt),
            this._retryConfig.maxDelayMs
        );
        // Add jitter: random value between 50% and 100% of calculated delay
        return delay * (0.5 + Math.random() * 0.5);
    }

    /**
     * Add AI-friendly pagination hint to results when the dataset is large.
     * Mutates the result array by attaching a _paginationHint property.
     * @param {Array} result - The array of records returned
     * @param {string} collectionName - Name of the collection
     * @param {object} options - The options that were used for the query
     */
    _addPaginationHintIfNeeded(result, collectionName, options) {
        if (!Array.isArray(result)) return;
        if (result.length < this._largeResultSetThreshold) return;

        const usedLimit = options?.limit;
        const usedOffset = options?.offset || 0;

        result._paginationHint = {
            message:
                `Large result set detected for "${collectionName}": ${result.length} records returned. ` +
                `For better performance, use pagination with limit and offset parameters. ` +
                `Example: db.list("${collectionName}", sort, 50, 0) for the first page, ` +
                `db.list("${collectionName}", sort, 50, 50) for the second page.`,
            recordCount: result.length,
            currentOffset: usedOffset,
            currentLimit: usedLimit || "none (all records)",
            suggestion:
                "Use limit and offset to paginate through records instead of fetching all at once.",
        };

        console.warn(`[DB Performance] ${result._paginationHint.message}`);
    }

    /**
     * Call the database API with specified operation
     * @param {string} operation - The operation type (insert, update, delete, getmany, etc.)
     * @param {string} tableName - Name of the table/collection
     * @param {object} data - Data for the operation
     * @param {object} where - Where conditions (MongoDB-style operators supported)
     * @param {array} records - Array of records for bulk operations
     * @param {object} options - Additional options: { sort, limit, offset, apiKey, authToken }
     */
    async callApi(
        operation,
        tableName,
        data = null,
        where = null,
        records = null,
        options = null
    ) {
        try {
            if (!this.workflowId) {
                throw new Error(
                    "Workflow ID is required. Make sure workflowId is configured."
                );
            }

            // At the start of callApi, if this is a write operation, clear the cache for this table
            const writeOperations = [
                "insert",
                "update",
                "delete",
                "bulkinsert",
                "bulkupdate",
                "increment",
            ];
            if (
                writeOperations.includes(operation.toLowerCase()) &&
                tableName
            ) {
                this._clearCacheForTable(tableName);
            }

            // Generate call signature for deduplication and caching
            const callSignature = this._generateCallSignature(
                operation,
                tableName,
                data,
                where,
                records,
                options
            );
            const now = this._now();

            // Increment is the one non-idempotent operation: two `+1` calls
            // are two distinct intents, not a duplicate. Serving the second
            // from the 300ms result cache — or handing it the first's
            // in-flight promise via the 50ms dedup window — would silently
            // drop a count. Both layers are bypassed; the server-side
            // requestId guard handles genuine retries instead.
            const bypassCallCache = operation.toLowerCase() === "increment";

            // Skip caching in backend (Deno) - only use cache in browser
            if (!isDeno && !bypassCallCache) {
                // Clean up expired entries periodically (moved to top to match User.js behavior)
                this._cleanupExpiredCalls();

                // First, check the cache (300ms window) - return cached result if available
                const cachedResult = this._resultCacheMap.get(callSignature);
                if (
                    cachedResult &&
                    now - cachedResult.timestamp < this._cacheTimeoutMs
                ) {
                    // Return cached result immediately
                    return cachedResult.result;
                }

                // Second, check for duplicate calls within debounce window (50ms)
                // This prevents duplicate API calls when the same call is made rapidly
                const existingCall =
                    this._callDeduplicationMap.get(callSignature);
                if (
                    existingCall &&
                    now - existingCall.timestamp < this._debounceTimeoutMs
                ) {
                    // Return the existing promise if call was made within debounce window
                    return existingCall.promise;
                }
            }

            // Check rate limit for this entity - only check if we're actually making the call
            const entityName = tableName || "unknown";

            // Detect rapid successive read calls (search-as-you-type without debounce)
            if (operation.toLowerCase() === "getmany" && isBrowser) {
                this._detectRapidCalls(entityName, now, where);
            }

            // Take a token, or reject here and now with retryAfter. Synchronous:
            // it must not break the dedup lookup → registration span below, and
            // a caller that is over budget deserves the answer immediately.
            this._acquireSlot(entityName);

            // Get credentials - priority: explicit options > instance credentials > auto-detected
            const apiKey =
                options?.apiKey || this._explicitApiKey || this.getApiKey();
            const token =
                options?.authToken ||
                this._explicitAuthToken ||
                this.getToken();

            // Build the endpoint URL — prefer Lambda Function URL (no 1MB limit),
            // fall back to a secondary Lambda URL, then to the API/ALB route.
            const lambdaUrl =
                typeof window !== "undefined"
                    ? window.__ENV__?.DATABASE_RECORDS_LAMBDA_URL
                    : typeof process !== "undefined"
                    ? process.env.DATABASE_RECORDS_LAMBDA_URL
                    : null;
            const lambdaFallbackUrl =
                typeof window !== "undefined"
                    ? window.__ENV__?.DATABASE_RECORDS_FALLBACK_URL
                    : typeof process !== "undefined"
                    ? process.env.DATABASE_RECORDS_FALLBACK_URL
                    : null;
            const buildRecordsUrl = (base) =>
                `${base.replace(/\/$/, "")}/workflows/${
                    this.workflowId
                }/auth/database/records`;
            const ecsFallbackEndpoint = `${this.apiUrl}/workflows/${this.workflowId}/auth/database/records`;
            let endpoint = lambdaUrl
                ? buildRecordsUrl(lambdaUrl)
                : ecsFallbackEndpoint;
            // Ordered chain to try if the primary endpoint fails. Each entry is
            // distinct from the primary so we don't retry the same URL.
            const fallbackChain = [];
            if (
                lambdaFallbackUrl &&
                lambdaFallbackUrl !== lambdaUrl &&
                endpoint !== buildRecordsUrl(lambdaFallbackUrl)
            ) {
                fallbackChain.push(buildRecordsUrl(lambdaFallbackUrl));
            }
            if (endpoint !== ecsFallbackEndpoint) {
                fallbackChain.push(ecsFallbackEndpoint);
            }

            // For delete operations, add important info to query parameters
            // This ensures compatibility with proxies that might strip DELETE bodies
            if (operation === "delete" && (data?.key || where?.key)) {
                const url = new URL(endpoint);
                const key = data?.key || where?.key;
                if (key) url.searchParams.append("key", key);
                if (tableName) url.searchParams.append("tableName", tableName);
                endpoint = url.toString();
            }

            // Build payload matching insertWorkflowDatabaseRecord expectations
            const payload = {
                tableName: tableName,
                operation: operation,
            };

            if (data) {
                payload.data = data;
            }

            if (where) {
                payload.where = where;
            }

            if (records) {
                payload.records = records;
            }

            // Add sort, limit, offset if provided (for backend processing)
            if (options?.sort !== undefined) {
                payload.sort = options.sort;
            }

            if (options?.limit !== undefined) {
                payload.limit = options.limit;
            }

            if (options?.offset !== undefined) {
                payload.offset = options.offset;
            }

            // Build headers
            const headers = {
                "Content-Type": "application/json",
            };

            // Add authentication - Bearer token for browser, API key for backend
            if (token) {
                headers["Authorization"] = `Bearer ${token}`;
            }

            const serverKey = this.getServerKey();
            if (serverKey) {
                headers["x-server-key"] = serverKey;
            }

            if (apiKey) {
                headers["x-api-key"] = apiKey;
            }

            // Which environment this backend runs in, with the attestation
            // that proves it. Sent ONLY alongside this request's own s2s token
            // — the attestation is bound to that token, and a call that
            // carries the caller's environment apiKey is already scoped by it.
            //
            // Without this, a function deployed to an environment reads and
            // writes the PRODUCTION database: the s2s secret is shared by
            // every environment of the app, so the token alone cannot say
            // which one it came from. A server that does not know these
            // headers ignores them and behaves exactly as it does today.
            if (isDeno) {
                const context = this._getRequestContext();
                if (
                    context?.environmentName &&
                    context?.environmentSignature &&
                    apiKey &&
                    apiKey === context.s2sToken
                ) {
                    headers["x-agentui-environment"] = context.environmentName;
                    headers["x-agentui-environment-sig"] =
                        context.environmentSignature;
                }
            }

            // Create the API call promise with retry logic
            const fetchOptions = {
                method: "POST",
                headers,
                body: JSON.stringify(payload),
            };

            const apiCallPromise = (async () => {
                let lastError = null;
                const maxAttempts = this._retryConfig.maxRetries + 1;

                for (let attempt = 0; attempt < maxAttempts; attempt++) {
                    try {
                        // Use fetch with timeout
                        const response = await this._fetchWithTimeout(
                            endpoint,
                            fetchOptions
                        );

                        if (!response.ok) {
                            const errorText = await response
                                .text()
                                .catch(() => "");
                            let errorData = {};
                            try {
                                errorData = errorText
                                    ? JSON.parse(errorText)
                                    : {};
                            } catch (e) {
                                // Not JSON
                            }
                            const err = new Error(
                                errorData.error ||
                                    `HTTP error ${response.status} on ${operation} ${tableName}`
                            );
                            err._dbMeta = {
                                httpStatus: response.status,
                                responseBody: (
                                    errorData.error ||
                                    errorText ||
                                    ""
                                ).slice(0, 500),
                                endpoint,
                            };

                            // Check if retryable and we have retries left
                            if (
                                this._isRetryableError(err, response.status) &&
                                attempt < maxAttempts - 1
                            ) {
                                const delay = this._getRetryDelay(attempt);
                                console.warn(
                                    `[DB Retry] ${operation} ${tableName} failed (HTTP ${
                                        response.status
                                    }), retrying in ${Math.round(
                                        delay
                                    )}ms (attempt ${attempt + 1}/${
                                        this._retryConfig.maxRetries
                                    })`
                                );
                                await this._sleep(delay);
                                continue;
                            }

                            throw err;
                        }

                        // Handle 204 No Content
                        if (response.status === 204) {
                            return null;
                        }

                        // Safely parse JSON from response text
                        const text = await response.text();
                        const responseData = text ? JSON.parse(text) : {};

                        // Handle response - check for error status first
                        if (
                            responseData?.status &&
                            responseData.status >= 400
                        ) {
                            const err = new Error(
                                responseData.error ||
                                    `Database ${operation} failed on ${tableName} (status ${responseData.status})`
                            );
                            err._dbMeta = {
                                httpStatus: responseData.status,
                                responseBody:
                                    responseData.error ||
                                    JSON.stringify(responseData).slice(0, 500),
                                endpoint,
                            };
                            throw err;
                        }

                        // Handle response - the API may return data directly or wrapped in a response object
                        if (responseData?.data !== undefined) {
                            const recordCount = Array.isArray(responseData.data)
                                ? responseData.data.length
                                : 0;
                            if (recordCount >= 2000) {
                                const hasMore = responseData.hasMore;
                                const suggestion = `The "${tableName}" table returned ${recordCount} records${
                                    hasMore ? " and more exist" : ""
                                }. Add pagination to this view — show 50 records per page with next/previous buttons to improve load times and make the data easier to browse.`;
                                if (typeof console.human === "function") {
                                    console.human(suggestion, {
                                        type: "pagination_suggestion",
                                        tableName,
                                        returnedCount: recordCount,
                                        hasMore,
                                        limit: responseData.limit,
                                        offset: responseData.offset,
                                        suggestion,
                                        actionable: true,
                                    });
                                } else {
                                    console.log(
                                        `[Database] "${tableName}" returned ${recordCount} records. Consider adding pagination.`
                                    );
                                }
                            }
                            return responseData.data;
                        }
                        return responseData;
                    } catch (error) {
                        lastError = error;

                        // Don't retry non-retryable errors or if out of retries
                        const httpStatus = error._dbMeta?.httpStatus;
                        if (
                            !this._isRetryableError(error, httpStatus) ||
                            attempt >= maxAttempts - 1
                        ) {
                            throw error;
                        }

                        const delay = this._getRetryDelay(attempt);
                        console.warn(
                            `[DB Retry] ${operation} ${tableName} failed (${
                                error.message
                            }), retrying in ${Math.round(delay)}ms (attempt ${
                                attempt + 1
                            }/${this._retryConfig.maxRetries})`
                        );
                        await this._sleep(delay);
                    }
                }

                // Should not reach here, but safety net
                throw lastError;
            })();

            // Store the promise in deduplication map (browser only)
            if (!isDeno && !bypassCallCache) {
                this._callDeduplicationMap.set(callSignature, {
                    promise: apiCallPromise,
                    timestamp: now,
                });
            }

            // Wait for the promise and cache the result on success
            try {
                let result;
                try {
                    result = await apiCallPromise;
                } catch (lambdaErr) {
                    // Walk the fallback chain (secondary Lambda URL → ECS API).
                    // First success wins; if all fail, rethrow the last error.
                    if (fallbackChain.length === 0) {
                        throw lambdaErr;
                    }
                    let fallbackSettled = false;
                    let lastFallbackErr = lambdaErr;
                    for (const fbEndpoint of fallbackChain) {
                        try {
                            console.warn(
                                `[DB Fallback] ${operation} ${tableName} failed at ${endpoint}, trying ${fbEndpoint}`,
                                lastFallbackErr.message
                            );
                            const fallbackResponse =
                                await this._fetchWithTimeout(
                                    fbEndpoint,
                                    fetchOptions
                                );
                            if (!fallbackResponse.ok) {
                                const errText = await fallbackResponse
                                    .text()
                                    .catch(() => "");
                                const err = new Error(
                                    `HTTP error ${fallbackResponse.status} on ${operation} ${tableName}`
                                );
                                err._dbMeta = {
                                    httpStatus: fallbackResponse.status,
                                    responseBody: errText.slice(0, 500),
                                    endpoint: fbEndpoint,
                                };
                                throw err;
                            }
                            if (fallbackResponse.status === 204) {
                                result = null;
                            } else {
                                const text = await fallbackResponse.text();
                                const responseData = text
                                    ? JSON.parse(text)
                                    : {};
                                if (
                                    responseData?.status &&
                                    responseData.status >= 400
                                ) {
                                    const err = new Error(
                                        responseData.error ||
                                            `Database ${operation} failed on ${tableName} (status ${responseData.status})`
                                    );
                                    err._dbMeta = {
                                        httpStatus: responseData.status,
                                        responseBody:
                                            responseData.error ||
                                            JSON.stringify(responseData).slice(
                                                0,
                                                500
                                            ),
                                        endpoint: fbEndpoint,
                                    };
                                    throw err;
                                }
                                result =
                                    responseData?.data !== undefined
                                        ? responseData.data
                                        : responseData;
                            }
                            fallbackSettled = true;
                            break;
                        } catch (fbErr) {
                            lastFallbackErr = fbErr;
                        }
                    }
                    if (!fallbackSettled) {
                        throw lastFallbackErr;
                    }
                }

                // Cache the successful result for 300ms (browser only).
                // Stamped on arrival, not at dispatch: a slow request would
                // otherwise write an entry already past its own TTL.
                if (!isDeno && !bypassCallCache) {
                    this._resultCacheMap.set(callSignature, {
                        result: result,
                        timestamp: this._now(),
                    });

                    // Remove from debounce map after successful completion
                    // (cache will handle subsequent calls within 300ms)
                    this._callDeduplicationMap.delete(callSignature);
                }

                return result;
            } catch (error) {
                // Remove from both maps on error so retries can work (browser only)
                if (!isDeno) {
                    this._callDeduplicationMap.delete(callSignature);
                    this._resultCacheMap.delete(callSignature);
                }
                throw error;
            }
        } catch (error) {
            console.error(`Database API error (${operation}):`, error);
            // Extract error message
            const errorMessage = error.message || "Unknown error";

            // Report to backend telemetry. A RATE_LIMITED rejection is NOT
            // reported here: _handleRateLimitExceeded already reports that
            // class once per window, and this path has no throttle at all — a
            // looping component would turn every rejection into an error-report
            // POST, which is exactly the situation that produced them.
            if (
                typeof window !== "undefined" &&
                window.reportErrorToBackend &&
                error.code !== "RATE_LIMITED"
            ) {
                const dbMeta = error._dbMeta || {};
                window.reportErrorToBackend(errorMessage, "DATABASE_ERROR", {
                    operation,
                    tableName,
                    apiUrl: dbMeta.endpoint || this.apiUrl,
                    httpStatus:
                        dbMeta.httpStatus ||
                        error.status ||
                        error.response?.status,
                    responseBody: dbMeta.responseBody,
                    stack: error.stack?.slice(0, 1000),
                });
            }

            const wrapped = new Error(errorMessage);
            // The outer wrapper must not strip the backoff contract — a caller
            // that cannot see retryAfter has no way to retry correctly.
            if (error.code) wrapped.code = error.code;
            if (error.retryAfter !== undefined) {
                wrapped.retryAfter = error.retryAfter;
                wrapped.retryAfterMs = error.retryAfterMs;
                wrapped.entity = error.entity;
            }
            throw wrapped;
        }
    }

    /**
     * List all records from a collection
     * @param {string} collectionName - Name of the collection/table
     * @param {string|object|array} sort - Sort specification:
     *   - String: "fieldName" (ASC) or "-fieldName" (DESC)
     *   - Object: { field: "asc" }, { field: "desc" }, { field: 1 }, { field: -1 }
     *   - Extended: { field: "score", direction: "asc", type: "number" } for typed sort
     *   - Array: ["field1", "-field2"] or [{ field: "score", direction: "desc", type: "number" }]
     * @param {number} limit - Maximum number of records to return
     * @param {number} offset - Number of records to skip (for pagination)
     */
    async list(collectionName, sort = null, limit = null, offset = null) {
        try {
            const options = {};
            if (sort !== null) options.sort = sort;
            if (limit !== null) options.limit = limit;
            if (offset !== null) options.offset = offset;

            const result = await this.callApi(
                "getmany",
                collectionName,
                null,
                null,
                null,
                Object.keys(options).length > 0 ? options : null
            );

            this._addPaginationHintIfNeeded(result, collectionName, options);
            return result;
        } catch (error) {
            console.error(`Error listing ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Filter records based on conditions
     * All filtering, sorting, and pagination is done on the backend for better performance.
     *
     * Supports MongoDB-like operators:
     * - Comparison: $eq, $ne, $gt, $gte, $lt, $lte
     * - Array: $in, $nin
     * - String: $contains, $like, $icontains, $ilike, $startsWith, $endsWith
     * - Regex: $regex
     * - Logical: $or, $and, $not
     * - Existence: $exists
     *
     * @param {string} collectionName - Name of the collection/table
     * @param {object} conditions - Filter conditions (MongoDB-style operators supported)
     * @param {string|object|array} sort - Sort specification:
     *   - String: "fieldName" (ASC) or "-fieldName" (DESC)
     *   - Object: { field: "asc" }, { field: "desc" }, { field: 1 }, { field: -1 }
     *   - Extended: { field: "score", direction: "asc", type: "number" } for typed sort
     *   - Array: ["field1", "-field2"] or [{ field: "score", direction: "desc", type: "number" }]
     * @param {number} limit - Maximum number of records to return
     * @param {number} offset - Number of records to skip (for pagination)
     */
    async filter(
        collectionName,
        conditions,
        sort = null,
        limit = null,
        offset = null
    ) {
        try {
            const options = {};
            if (sort !== null) options.sort = sort;
            if (limit !== null) options.limit = limit;
            if (offset !== null) options.offset = offset;

            const result = await this.callApi(
                "getmany",
                collectionName,
                null,
                conditions,
                null,
                Object.keys(options).length > 0 ? options : null
            );

            this._addPaginationHintIfNeeded(result, collectionName, options);
            return result;
        } catch (error) {
            console.error(`Error filtering ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Find a record by ID
     */
    async findById(collectionName, id) {
        try {
            const result = await this.callApi(
                "getmany",
                collectionName,
                {},
                { key: id }
            );

            const records = result;

            if (Array.isArray(records) && records.length > 0) {
                return records[0];
            }

            return null;
        } catch (error) {
            console.error(`Error finding ${collectionName} by ID:`, error);
            throw error;
        }
    }

    /**
     * Count records in a collection, optionally filtered by conditions
     * @param {string} collectionName - Name of the collection/table
     * @param {object} conditions - Filter conditions (MongoDB-style operators supported)
     * @returns {Promise<number>} - The count of matching records
     */
    async count(collectionName, conditions = null) {
        try {
            const result = await this.callApi(
                "count",
                collectionName,
                null,
                conditions
            );

            // Backend returns { count: N }
            if (result && typeof result.count === "number") {
                return result.count;
            }

            // Fallback: if result is a number directly
            if (typeof result === "number") {
                return result;
            }

            return 0;
        } catch (error) {
            console.error(`Error counting ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Aggregate records with server-side computation.
     * @param {string} collectionName - Name of the collection/table
     * @param {object} options - { metrics, groupBy, filters, having, orderBy, limit, view, refresh }
     * @returns {Promise<object[]>} - Array of aggregate result rows
     */
    async aggregate(collectionName, options = {}) {
        try {
            // View-only read: no metrics needed
            if (options.view && !options.metrics) {
                const result = await this.callApi("aggregate", collectionName, {
                    view: options.view,
                    refresh: options.refresh || false,
                });
                if (result && Array.isArray(result.rows)) {
                    return result.rows;
                }
                if (Array.isArray(result)) {
                    return result;
                }
                return [];
            }

            // Standard or view-with-definition: metrics required
            if (
                !options.metrics ||
                !Array.isArray(options.metrics) ||
                options.metrics.length === 0
            ) {
                throw new Error(
                    'aggregate() requires a "metrics" array with at least one metric. ' +
                        "Each metric needs { field, op, as }. Supported ops: sum, avg, count, min, max. " +
                        'Example: { metrics: [{ field: "amount", op: "sum", as: "total" }] }'
                );
            }

            const validOps = ["sum", "avg", "count", "min", "max"];
            for (const m of options.metrics) {
                if (!m.op || !validOps.includes(m.op)) {
                    throw new Error(
                        `Invalid metric op "${
                            m.op
                        }". Must be one of: ${validOps.join(", ")}. ` +
                            `Usage: { field: "amount", op: "sum", as: "total_amount" }`
                    );
                }
                if (m.op !== "count" && !m.field) {
                    throw new Error(
                        `Metric with op "${m.op}" requires a "field". ` +
                            `Usage: { field: "amount", op: "${m.op}", as: "result" }`
                    );
                }
                if (!m.as) {
                    throw new Error(
                        'Each metric requires an "as" alias for the result. ' +
                            'Usage: { field: "amount", op: "sum", as: "total_amount" }'
                    );
                }
            }

            if (
                options.having &&
                options.having.length > 0 &&
                (!options.groupBy || options.groupBy.length === 0)
            ) {
                throw new Error(
                    '"having" requires "groupBy". HAVING filters aggregate results by group. ' +
                        "Add a groupBy array or remove the having clause."
                );
            }

            const result = await this.callApi(
                "aggregate",
                collectionName,
                options
            );

            // The server flags a metric that came back null in EVERY row and
            // says why. aggregate() returns a bare array, so without this the
            // explanation is dropped on the floor and the caller is left
            // staring at an empty column with a 200 next to it.
            if (result && Array.isArray(result.warnings)) {
                for (const warning of result.warnings) {
                    console.warn(`aggregate(${collectionName}): ${warning}`);
                }
            }

            if (result && Array.isArray(result.rows)) {
                return result.rows;
            }
            if (Array.isArray(result)) {
                return result;
            }
            return [];
        } catch (error) {
            console.error(`Error aggregating ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Create a new record
     */
    async create(collectionName, data) {
        try {
            const result = await this.callApi("insert", collectionName, data);
            if (result === null) return null;
            return result.record || result.data || result;
        } catch (error) {
            console.error(`Error creating ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Create multiple records at once (creates one by one)
     */
    async bulkCreate(collectionName, dataArray) {
        try {
            const result = await this.callApi(
                "bulkinsert",
                collectionName,
                null,
                null,
                dataArray
            );
            if (result === null) return null;
            return result.data || result;
        } catch (error) {
            console.error(`Error bulk creating ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Update multiple records at once via the backend bulkupdate operation.
     * Each entry must include `key` (or `id`) identifying the record, plus
     * the fields to update. Single round-trip.
     */
    async bulkUpdate(collectionName, recordsArray) {
        try {
            const result = await this.callApi(
                "bulkupdate",
                collectionName,
                null,
                null,
                recordsArray
            );
            if (result === null) return null;
            return result.data || result;
        } catch (error) {
            console.error(`Error bulk updating ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Update a record
     */
    async update(collectionName, id, updates) {
        try {
            // Add the key/id to the updates data
            const dataWithKey = { ...updates, key: id };
            const result = await this.callApi(
                "update",
                collectionName,
                dataWithKey,
                { key: id }
            );
            if (result === null) return null;
            return result.record || result.data || result;
        } catch (error) {
            console.error(`Error updating ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Generate a request id used to make one increment exactly-once across
     * the retry loop above. Must be created ONCE per logical increment —
     * every retry of the same call reuses the same payload, and therefore
     * the same id, which is exactly what lets the server dedup replays
     * without deduping two genuine +1s.
     */
    _newRequestId() {
        if (
            typeof crypto !== "undefined" &&
            typeof crypto.randomUUID === "function"
        ) {
            return crypto.randomUUID();
        }
        return `${Date.now().toString(36)}-${Math.random()
            .toString(36)
            .slice(2, 12)}`;
    }

    /**
     * Atomically increment numeric fields on a record.
     *
     * Unlike update(), the new value is computed inside Postgres, so
     * concurrent callers on different servers cannot lose each other's
     * changes.
     *
     * @param {string} collectionName
     * @param {string} id - Record key
     * @param {object} increments - { field: signedDelta }
     * @param {object} [options] - { guards: { field: { min, max } } }
     */
    async increment(collectionName, id, increments, options = null) {
        try {
            const payload = {
                key: id,
                increments,
                requestId: this._newRequestId(),
            };
            if (options?.guards) payload.guards = options.guards;

            const result = await this.callApi(
                "increment",
                collectionName,
                payload,
                { key: id }
            );
            if (result === null) return null;
            return result.record || result.data || result;
        } catch (error) {
            console.error(`Error incrementing ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Delete a record
     */
    async delete(collectionName, id) {
        try {
            const result = await this.callApi("delete", collectionName, {
                key: id,
            });
            // If result is null (e.g. 204 No Content), consider it successful
            if (result === null) return true;
            return result.success !== false;
        } catch (error) {
            console.error(`Error deleting ${collectionName}:`, error);
            throw error;
        }
    }
}

// Create and export singleton instance
const dbAdapter = new DatabaseAdapter();
export { DatabaseAdapter };
export default dbAdapter;
